import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { ensureArray } from '@/lib/utils/ensure-array';

interface ModuleRow {
  moduleId: number | string;
  moduleName?: string;
}

const FIVE_MIN = 5 * 60 * 1000;

/**
 * Returns the set of moduleIds the **currently logged-in user** has been
 * granted, used to gate the sidebar and any module-conditional UI.
 *
 * For SADMIN / SUB_SUPER_ADMIN this returns `null` (the sentinel for
 * "do not gate by modules" — those roles get everything they're allowed
 * to see by role + ACL, not by module entitlement). Use the value as
 * `if (modules === null || modules.has(id))` at the gate.
 */
export function useCurrentUserModules(): {
  modules: Set<number> | null;
  isLoading: boolean;
} {
  const role = useAuthStore((s) => s.role);
  const userId = useAuthStore((s) => s.userId);

  // SADMIN + SUB_SUPER_ADMIN bypass module gating — they manage modules,
  // not consume them. ACL filters their menu separately.
  const skip =
    role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;

  const query = useQuery({
    queryKey: ['current-user', 'modules', userId],
    queryFn: () =>
      api.get<ModuleRow[]>(`/api/obd/module/list/${userId}`),
    enabled: Boolean(userId) && !skip,
    staleTime: FIVE_MIN,
    select: (d) => ensureArray<ModuleRow>(d),
  });

  return useMemo(() => {
    if (skip) return { modules: null, isLoading: false };
    if (query.isLoading || !query.data) {
      return { modules: new Set<number>(), isLoading: query.isLoading };
    }
    const ids = new Set<number>();
    for (const row of query.data) {
      const id = typeof row.moduleId === 'string' ? Number(row.moduleId) : row.moduleId;
      if (Number.isFinite(id)) ids.add(id);
    }
    return { modules: ids, isLoading: false };
  }, [skip, query.isLoading, query.data]);
}
